import { Accordion, AccordionItem } from '@heroui/react';

import { agentLogDrawerSectionAccordionItemClasses } from './agentLogDrawerAccordionStyles';
import { renderMonitorExecutionLogCard } from './renderMonitorExecutionLogCard';

import type { AgentExecutionLogEntry } from '~/api/agentLogs/types';
import i18n from '~/common/helpers/i18n';

type AgentLogDetailsExecutionLogsSectionProps = {
  logs: AgentExecutionLogEntry[];
  isSectionExpanded: boolean;
  onSectionExpandedChange: (expanded: boolean) => void;
  expandedLogIds: Set<string>;
  onToggleLogExpanded: (logId: string, expanded: boolean) => void;
  nestedAccordionsExpanded: boolean;
  accordionEpoch: number;
};

export function AgentLogDetailsExecutionLogsSection({
  logs,
  isSectionExpanded,
  onSectionExpandedChange,
  expandedLogIds,
  onToggleLogExpanded,
  nestedAccordionsExpanded,
  accordionEpoch
}: AgentLogDetailsExecutionLogsSectionProps) {
  const hasLogs = logs.length > 0;

  return (
    <section data-testid='monitor-agent-logs-details-execution-logs'>
      <Accordion
        selectionMode='single'
        selectedKeys={
          isSectionExpanded ? new Set(['execution-logs']) : new Set<string>()
        }
        onSelectionChange={keys => {
          const selectedKeys =
            keys === 'all'
              ? new Set(['execution-logs'])
              : (keys as Set<string>);
          onSectionExpandedChange(selectedKeys.has('execution-logs'));
        }}
        itemClasses={agentLogDrawerSectionAccordionItemClasses}
      >
        <AccordionItem
          key='execution-logs'
          aria-label={i18n.t('scenes.monitor.agentLogs.details.executionLogs')}
          title={
            <span className='text-label-lg font-semibold text-on-surface'>
              {i18n.t('scenes.monitor.agentLogs.details.executionLogs')}
            </span>
          }
        >
          {!hasLogs ? (
            <p className='text-body-regular-md text-on-surface-variant'>
              {i18n.t('scenes.monitor.agentLogs.details.noExecutionLogs')}
            </p>
          ) : (
            <div
              className='flex flex-col gap-3'
              data-testid='monitor-agent-logs-details-logs-list'
            >
              {logs.map(log => {
                const card = renderMonitorExecutionLogCard({
                  log,
                  isExpanded: expandedLogIds.has(log.id),
                  onExpandedChange: expanded =>
                    onToggleLogExpanded(log.id, expanded),
                  nestedAccordionSyncKey: accordionEpoch,
                  nestedAccordionSyncExpanded: nestedAccordionsExpanded
                });

                if (!card) {
                  return null;
                }

                return (
                  <div
                    key={`${log.id}-${accordionEpoch}`}
                    data-testid='monitor-agent-logs-details-log-item'
                  >
                    {card}
                  </div>
                );
              })}
            </div>
          )}
        </AccordionItem>
      </Accordion>
    </section>
  );
}
